/**
 * A full-screen phone surface with its own 56px header
 * (`design/Games Tonight Mobile.dc.html` :634-639).
 *
 * A SIBLING of `Sheet`, not a variant of it. `Sheet`'s frozen base is a bottom
 * sheet — `alignItems:'flex-end'`, `var(--gt-card)`, `borderBottom:0`,
 * `boxShadow:'0 -10px 0'`, the `gt-sheet` rise animation — and a full-screen
 * page has to override every one of those. At that point nothing of the frozen
 * spec is left, and the next reader cannot tell which parts of `Sheet` are still
 * load-bearing. Two small primitives are cheaper than one with a mode flag.
 *
 * THE BACKGROUND IS OPAQUE, NOT A SCRIM. `--gt-bg` rather than `--gt-overlay`,
 * because this surface replaces the page rather than sitting over it — it has to
 * cover the z50 bottom nav too, or the tab bar shows through a "full-screen"
 * view and taps land on the screen behind it.
 *
 * The 56px header and `--gt-bg-hd` are the app's own top bar, matched on
 * purpose: this reads as the same app one level deeper, not as a dialog.
 *
 * CALLERS MUST REGISTER THEIR OPEN FLAG in `store/history.ts`. Nothing here can
 * enforce that, and the cost of forgetting is high in a way that looks like two
 * unrelated bugs: the OS back gesture does not close the sheet, and the page
 * underneath keeps scrolling, because `overlayOpen` reads the same level list.
 */

import type { ReactNode } from 'react';

export function FullSheet({
  title,
  onClose,
  children,
  label,
  zIndex,
}: {
  /** Pixel-font heading, left-aligned in the header row. */
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** `data-screen-label`, kept from the prototype for screenshot tooling. */
  label?: string | undefined;
  /** 80 unless a caller needs to sit above another full-screen surface. */
  zIndex?: number | undefined;
}): JSX.Element {
  return (
    <div
      data-screen-label={label}
      style={{
        position: 'fixed',
        // NOT `inset: 0`. The mirror writes `inset:0 … left:50%;
        // transform:translateX(-50%)` (`Games Tonight Mobile.dc.html:634`) and
        // that combination is broken on any viewport wider than the 430 cap:
        // `inset` sets `right:0` too, `left:50%` overrides only the left, so the
        // box spans 50%→100% — half the screen — and the translate then slides
        // it to 25%→75%. It looks right in the prototype only because that frame
        // is exactly 430 wide. On a real phone reporting 448 CSS px it shipped
        // as a floating half-width column with the page visible on both sides.
        //
        // Stated explicitly instead: full height, horizontally centred, capped.
        top: 0,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 430,
        zIndex: zIndex ?? 80,
        background: 'var(--gt-bg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 8px 11px 14px',
          height: 56,
          boxSizing: 'border-box',
          background: 'var(--gt-bg-hd)',
          borderBottom: '1px solid var(--gt-border)',
          flex: 'none',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: 9.5,
            color: 'var(--gt-text)',
            transform: 'translateY(0.13em)',
          }}
        >
          {title}
        </span>
        <div style={{ flex: 1 }} />
        {/* 44×44 and bordered — the only way out of a surface with no backdrop
            to tap, so it is a full touch target rather than a bare glyph. */}
        <span
          onClick={onClose}
          data-tip="CLOSE"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            flex: 'none',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text-5)',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 14,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ✕
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
}
